import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { BASE_PATH as CONTENT_BASE_PATH } from '@cxb/content-stb-api';
import { ApiModule, BASE_PATH as EDITOR_BASE_PATH, Configuration, ConfigurationParameters } from '@cxb/editor-stb-api';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxDatabaseModule } from '@nx-data/nxg-database';
import { CONFIG_TOKEN } from 'config-token';
import {
  AbstractNewRelicService,
  ClientConfigApiEntity,
  ClientConfigService,
  CollectionPageEntity,
  CustomTranslateLoader,
  GenrePageEntity,
  MarketingDetailPageItemEntity,
  MenuEntity,
  OsnSharedModule,
  PageEntity,
  PageItemEntity,
  RecoTrayContentEntity,
  RedeemPageEntity,
  SellableSubscriptionsEntity,
  SimilarTitlesEntity,
  AbstractBookmarkService,
  AbstractNavigationService,
} from 'osn-shared';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppEffects } from './app.effects';
import { appReducer, combinedAppReducer, combinedFeatureKey } from './app.reducer';
import { Config } from './config';
import { CoreModule } from './core/core.module';
import { AuthGuard } from './core/guards/auth-guard.guard';
import { TenantGuard } from './core/guards/tenant.guard';
import { MockBookmarkService } from './core/services/mock-bookmark.service';
import { NavigationService } from './core/services/navigation.service';
import { NotificationModule } from './notification/notification.module';
import { PageEditScreenModule } from './page-edit-screen/page-edit-screen.module';
import { PageSelectorModule } from './shared/modules/page-selector/page-selector.module';
import { TIMEZONE } from './shared/pipes/cxb-date.pipe';
import { SharedModule } from './shared/shared.module';
import { AlwaysError } from './core/services/always-error.service';
import { strictStateImmutability } from '../environments/strictStateImmutability';

// Function expressions are not supported in decorators
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {};
  return new Configuration(params);
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function loggerCallback(logLevel: LogLevel, message: string) {
  // eslint-disable-next-line no-console
  // console.log(message);
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function MSALInstanceFactory(config: Config): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: config.ADClientId,
      authority: config.ADAuthority,
      redirectUri: config.appUri,
      postLogoutRedirectUri: `${config.appUri}/logout`,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: true,
      },
    },
  });
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Redirect };
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    OsnSharedModule,
    HttpClientModule,
    ApiModule.forRoot(apiConfigFactory),
    NotificationModule,
    PageEditScreenModule,
    PageSelectorModule,
    StoreModule.forRoot(
      { router: routerReducer, app: appReducer },
      {
        runtimeChecks: {
          strictStateImmutability,
        },
      }
    ),
    StoreModule.forFeature(combinedFeatureKey, combinedAppReducer),
    StoreRouterConnectingModule.forRoot({ navigationActionTiming: NavigationActionTiming.PostActivation }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    EffectsModule.forRoot([AppEffects]),
    MsalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient, ClientConfigService, AbstractNewRelicService],
      },
      defaultLanguage: 'en',
    }),
    SharedModule,
    NgxDatabaseModule.forRoot(
      ClientConfigApiEntity,
      CollectionPageEntity,
      GenrePageEntity,
      MarketingDetailPageItemEntity,
      MenuEntity,
      PageEntity,
      PageItemEntity,
      RecoTrayContentEntity,
      RedeemPageEntity,
      SimilarTitlesEntity,
      SellableSubscriptionsEntity
    ),
  ],
  providers: [
    {
      provide: SharedModule,
      deps: [TIMEZONE],
    },
    {
      provide: TIMEZONE,
      useFactory: (config: Config): string => config.timezone || '+0000',
      deps: [CONFIG_TOKEN],
    },
    {
      provide: EDITOR_BASE_PATH,
      useFactory: (config: Config): string => {
        return config.editorBackendRootUrl;
      },
      deps: [CONFIG_TOKEN],
    },
    {
      provide: CONTENT_BASE_PATH,
      useFactory: (config: Config): string => {
        return config.contentBackendRootUrl;
      },
      deps: [CONFIG_TOKEN],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (clientConfigService: ClientConfigService) => () => {
        // Do not wait on this promise for app initialization
        return clientConfigService.init().subscribe(() => {});
      },
      deps: [ClientConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [CONFIG_TOKEN],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: AbstractBookmarkService,
      useExisting: MockBookmarkService,
    },
    {
      provide: AbstractNavigationService,
      useExisting: NavigationService,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    AuthGuard,
    TenantGuard,
    MsalBroadcastService,
    AlwaysError,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
